import type { TaskRecord } from '../../containers/tasks-table/tasks-table.type';

enum AgentSource {
  CURSOR = 'cursor',
  CURSOR_CLI = 'cursor_cli',
  WINDSURF = 'windsurf',
  CHATGPT = 'chatgpt',
  CHATGPT_DESKTOP = 'chatgpt_desktop',
  CODEX = 'codex',
  CLAUDE = 'claude',
  CLAUDE_DESKTOP = 'claude_desktop',
  CLAUDE_CODE = 'claude_code',
  VS_CODE = 'vs_code',
  COPILOT = 'copilot',
  UNKNOWN = 'unknown',
}

enum AgentEventTypes {
  LLM_START = 'llm_start',
  LLM_END = 'llm_end',
  THINKING_START = 'thinking_start',
  THINKING_END = 'thinking_end',
  PLANNING_START = 'planning_start',
  PLANNING_END = 'planning_end',
  AGENT_CALL = 'agent_call',
  AGENT_RESULT = 'agent_result',
  AGENT_ERROR = 'agent_error',
  TOOL_CALL = 'tool_call',
  TOOL_RESULT = 'tool_result',
  TOOL_ERROR = 'tool_error',
  USER_PROMPT = 'user_prompt',
  HUMAN_INPUT_REQUESTED = 'human_input_requested',
  HUMAN_INPUT_RECEIVED = 'human_input_received',
  UNKNOWN = 'unknown',
}

enum AdherenceLogEventType {
  SKIPPED_STATUS = 'skipped_status',
  MISSED_EVENT = 'missed_event',
  UNEXPECTED_EVENT = 'unexpected_event',
}

type AgentEventMetadata = {
  user_prompt?: string;
  thought?: string;
  plan?: string;
  message?: string;
  agent_result?: Record<string, any>;
  agent_error?: string;
  tool_name?: string;
  tool_input?: Record<string, any>;
  tool_result?: Record<string, any>;
  tool_error?: string;
  human_input_requested?: string;
  human_input_received?: string;
};

type AgentEventUser = {
  id: string;
  email: string;
  name: string;
};

type AgentEventLog = {
  id: string;
  session_id: string;
  correlation_vector?: string;
  event_date: string;
  event_type: AgentEventTypes;
  agent_source: AgentSource;
  llm_model?: string;
  token_estimate?: number | null;
  metadata?: AgentEventMetadata;
  task_code?: string;
  user?: AgentEventUser;
  task?: TaskRecord;
};

type AgentSession = {
  session_id: string;
  start_date: string;
  end_date: string;
  correlation_vector: string;
  agent_source: AgentSource;
  llm_model?: string;
  num_events: number;
  num_user_prompts: number;
  num_tool_calls: number;
  user?: AgentEventUser;
};

type AdherenceLog = {
  id: string;
  task: TaskRecord;
  raw_event: string;
  event_type: AdherenceLogEventType;
  message: string;
  analysis: string;
  changed_at: string;
  changed_by: string;
  from_name: string;
  to_name: string;
};

type AgentFinancialOptionChartData = {
  cost: {
    total: number;
    by_agent_source: Record<AgentSource, number>;
  };
  tokens: {
    total: number;
    by_agent_source: Record<AgentSource, number>;
  };
};

type AgentFinancialOptionData = {
  id: string;
  name: string;
  code?: string;
  chart_data: Record<string, AgentFinancialOptionChartData>;
};

type AgentCostsSummary = {
  total_cost: number;
  total_tokens: number;
  total_sessions: number;
  active_users: number;
  median_cost_per_session: number;
};

type AgentFinancialResponse = {
  dates: string[];
  agent_sources: AgentSource[];
  tasks: AgentFinancialOptionData[];
  users: AgentFinancialOptionData[];
  epics: AgentFinancialOptionData[];
  initiatives: AgentFinancialOptionData[];
  summary?: AgentCostsSummary;
};

export { AdherenceLogEventType, AgentEventTypes, AgentSource };
export type {
  AdherenceLog,
  AgentCostsSummary,
  AgentEventLog,
  AgentFinancialOptionChartData,
  AgentFinancialOptionData,
  AgentFinancialResponse,
  AgentSession,
};
